import * as React from 'react';
import styled from 'styled-components';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers';
import * as yup from 'yup';
import {
  InputTextWithLabel,
  InputBaseContainer,
  InputSelectWithLabel,
  CheckboxWithLabel,
  ButtonWithLoader,
} from '../molecules';
import { FormServerError } from '../types';
import isYupFieldRequired from '../utils/isYupFieldRequired';
import {
  InspectionType,
  InspectionTableType,
  getInspectionTypeByID,
} from '../api/inspectionTypes';
import { api } from '../api/api';
import transformServerErrorsToForm from '../utils/transformServerErrorsToForm';
import { getJurisdictions } from '../api/jurisdiction';
import { getTrades } from '../api/trades';
import { GET_ALL_QUERY_PARAMS } from '../constants';
import useUserRole from '../hooks/useUserRole';
import { getFranchiseList } from '../api/franchise';

const labels = {
  name: 'Name',
  jurisdictions: 'Jurisdictions',
  trades: 'Trades',
  isActive: 'Active',
  isDefault: 'Set as default in new Branch',
  threshold: 'Threshold',
};

const schema = yup.object().shape({
  name: yup.string().required().label(labels.name),
  jurisdictions: yup
    .object()
    .shape({
      value: yup.string(),
      label: yup.string(),
    })
    .required()
    .typeError('Select a Jurisdictions from the list')
    .label(labels.jurisdictions),
  trades: yup
    .object()
    .shape({
      value: yup.string(),
      label: yup.string(),
    })
    .required()
    .typeError('Select a Trade from the list')
    .label(labels.trades),
  isActive: yup.boolean().required().label(labels.isActive),
  isDefault: yup.boolean().label(labels.isDefault),
});

const InspectionTypeForm = ({
  onSubmit,
  modalError,
  initialValues,
  addButtonText = 'Add new item',
  updateButtonText = 'Update',
  initialErrors,
}: Props) => {
  const {
    register,
    handleSubmit,
    control,
    errors,
    setError,
    setValue,
    watch,
    formState,
  } = useForm<InspectionType>({
    resolver: yupResolver(schema),
    defaultValues: initialValues || ({} as any),
  });

  const { isSuperAdmin } = useUserRole();

  const isDeleted = initialValues?.isDeleted;

  const [isSubmitting, setIsSubmitting] = React.useState<
    boolean
  >(false);

  const [isLoading, setIsLoading] = React.useState<boolean>(
    false,
  );

  React.useEffect(() => {
    if (isSuperAdmin) {
      setIsLoading(true);

      Promise.all([
        getFranchiseList({
          ...GET_ALL_QUERY_PARAMS,
          ordering: 'name',
        }),
      ])
        .then(([franchisesResponse]) => {
          const franchises = franchisesResponse.data.results.map(
            item => item.id,
          );

          setValue(
            'franchise',
            franchises.filter(Boolean).length
              ? franchises
              : undefined,
          );
        })
        .catch(() => {})
        .finally(() => setIsLoading(false));
    }
  }, []);

  React.useEffect(() => {
    if (
      formState.isSubmitting &&
      !Object.keys(errors).length
    ) {
      setIsSubmitting(formState.isSubmitting);
    }
  }, [formState]);

  const [
    jurisdictionsData,
    setJurisdictionsData,
  ] = React.useState([]);
  const [tradesData, setTradesData] = React.useState([]);
  const [
    inspectionTypeData,
    setInspectionTypeData,
  ] = React.useState(null);

  const [inspectionReports, setInspectionReports] = React.useState([]);
  const isThresholdChecked = watch('isThreshold');

  React.useEffect(() => {
    if (isThresholdChecked) {
      api.request({
        method: 'GET',
        url: '/inspection-types/available-inspection-reports/',
      }).then((res: any) => {
        setInspectionReports(res.data || []);
      }).catch(() => setInspectionReports([]));
    }
  }, [isThresholdChecked]);

  React.useEffect(() => {
    setIsLoading(true);

    Promise.all([
      getJurisdictions({
        ...GET_ALL_QUERY_PARAMS,
        ordering: 'name',
      }),
      getTrades({
        ...GET_ALL_QUERY_PARAMS,
        ordering: 'name',
      }),
    ])
      .then(([jurisdictionsResponse, tradesResponse]) => {
        setJurisdictionsData(
          jurisdictionsResponse.data.results,
        );
        setTradesData(
          tradesResponse.data.results.filter(
            trade => trade.name !== 'Residential',
          ),
        );
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, []);

  React.useEffect(() => {
    if (initialValues?.id) {
      getInspectionTypeByID(initialValues.id)
        .then(response =>
          setInspectionTypeData(response.data))
        .catch(() => {});
    }
  }, [initialValues]);

  React.useEffect(() => {
    if (inspectionTypeData && tradesData.length) {
      setValue(
        'jurisdictions',
        jurisdictionsData.find(
          item => item.id === initialValues.jurisdictionId,
        ),
      );
      setValue(
        'trades',
        tradesData.find(item =>
          inspectionTypeData.trades.includes(item.id)),
      );
      if (inspectionTypeData.linkedInspectionReport) {
        setValue('inspectionReportId', inspectionTypeData.linkedInspectionReport);
      }
    }
  }, [inspectionTypeData, jurisdictionsData, tradesData]);

  React.useEffect(() => {
    if (initialErrors) {
      transformServerErrorsToForm<InspectionType>(
        initialErrors,
      ).forEach(({ name, types }) =>
        setError(name, { types }));
    }
  }, [initialErrors]);

  React.useEffect(() => {
    if (Object.keys(errors).length) {
      setIsSubmitting(false);
    }
  }, [errors, initialErrors]);

  const middleware = (
    submitFn: (data: InspectionType) => void,
  ) => (data: InspectionType) => {
    const jurisdiction = data.jurisdictions as any;
    const trade = data.trades as any;
    const reportSelection = (data as any).inspectionReportId;
    const isThreshold = (data as any).isThreshold;

    if (isThreshold && !reportSelection) {
      setError('inspectionReportId' as any, {
        type: 'required',
        message: 'Inspection Report is required when Threshold is checked',
      });
      return;
    }

    submitFn({
      ...data,
      id: initialValues?.id,
      isDeleted: initialValues?.isDeleted,
      jurisdiction: jurisdiction?.id,
      trades:
        trade?.id ? [trade.id] : undefined,
      franchise: data.franchise
        ? (data.franchise as string)
            .split(',')
            .map(parseFloat)
        : undefined,
      inspectionReportId: reportSelection?.id || null,
    } as any);
  };

  return (
    <Container
      disabled={
        isLoading ||
        isDeleted ||
        (isSubmitting && !modalError)
      }
    >
      <FormContainer
        autoComplete="off"
        aria-autocomplete="none"
        onSubmit={handleSubmit(middleware(onSubmit))}
      >
        <InputBaseContainer>
          <InputTextWithLabel
            name="name"
            error={errors.name}
            inputRef={register}
            label={labels.name}
            isRequired={isYupFieldRequired('name', schema)}
          />
        </InputBaseContainer>
        <InputBaseContainer>
          <Controller
            name="jurisdictions"
            defaultValue=""
            control={control}
            render={props => (
              <InputSelectWithLabel
                placeholder=""
                isLoading={isLoading}
                isDisabled={isLoading}
                label={labels.jurisdictions}
                error={errors.jurisdictions}
                getOptionValue={(item: any) =>
                  item.id || Math.random()
                }
                getOptionLabel={(item: any) => item.name}
                isRequired={isYupFieldRequired(
                  'jurisdictions',
                  schema,
                )}
                options={jurisdictionsData}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
              />
            )}
          />
        </InputBaseContainer>
        <InputBaseContainer>
          <Controller
            name="trades"
            defaultValue=""
            control={control}
            render={props => (
              <InputSelectWithLabel
                placeholder=""
                isLoading={isLoading}
                isDisabled={isLoading}
                label={labels.trades}
                error={errors.trades}
                getOptionValue={(item: any) =>
                  item.id || Math.random()
                }
                getOptionLabel={(item: any) => item.name}
                isRequired={isYupFieldRequired(
                  'trades',
                  schema,
                )}
                options={tradesData}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
              />
            )}
          />
        </InputBaseContainer>
        <InputBaseContainer>
          <Controller
            name="isActive"
            control={control}
            valueName="checked"
            defaultValue={
              initialValues ? initialValues.isActive : true
            }
            render={({ onChange, onBlur, value }) => (
              <CheckboxWithLabel
                error={errors.isActive}
                label={labels.isActive}
                checked={value}
                onBlur={onBlur}
                onChange={(e, flag) => onChange(flag)}
              />
            )}
          />
        </InputBaseContainer>
        <InputBaseContainer>
          <Controller
            name="isThreshold"
            control={control}
            valueName="checked"
            defaultValue={
              initialValues ? initialValues.isThreshold : false
            }
            render={({ onChange, onBlur, value }) => (
              <CheckboxWithLabel
                label={labels.threshold}
                checked={value}
                onBlur={onBlur}
                onChange={(e, flag) => onChange(flag)}
              />
            )}
          />
        </InputBaseContainer>
        {isThresholdChecked && (
          <InputBaseContainer>
            <Controller
              name="inspectionReportId"
              defaultValue={initialValues?.linkedInspectionReport?.id || ''}
              control={control}
              rules={{ required: 'Inspection Report is required when Threshold is checked' }}
              render={props => (
                <InputSelectWithLabel
                  placeholder=""
                  label="Inspection Report"
                  getOptionValue={(item: any) => item.id}
                  getOptionLabel={(item: any) => item.name}
                  options={inspectionReports}
                  error={errors.inspectionReportId}
                  isRequired
                  {...props}
                />
              )}
            />
          </InputBaseContainer>
        )}
        {isSuperAdmin && (
          <InputBaseContainer>
            <Controller
              name="isDefault"
              control={control}
              valueName="checked"
              defaultValue={
                initialValues?.isDefault ?? true
              }
              render={({ onChange, onBlur, value }) => (
                <CheckboxWithLabel
                  error={errors.isDefault}
                  label={labels.isDefault}
                  checked={value}
                  onBlur={onBlur}
                  onChange={(e, flag) => onChange(flag)}
                />
              )}
            />
          </InputBaseContainer>
        )}

        <input
          type="hidden"
          name="franchise"
          ref={register}
        />

        {!isDeleted && (
          <ButtonWithLoader
            disabled={isSubmitting && !modalError}
            loading={isSubmitting && !modalError}
            type="submit"
          >
            {initialValues
              ? updateButtonText
              : addButtonText}
          </ButtonWithLoader>
        )}
      </FormContainer>
    </Container>
  );
};

const Container = styled.fieldset`
  display: block;
  margin: 0;
  padding: 0;
  width: 100%;
  border: none;
  text-align: center;
`;

const FormContainer = styled.form`
  width: 100%;

  .MuiButton-root {
    margin-top: 24px;
  }
`;

interface Props {
  addButtonText?: string;
  updateButtonText?: string;
  initialValues?: InspectionTableType;
  onSubmit: (data: InspectionType) => void;
  initialErrors?: FormServerError<InspectionType>;
  modalError: boolean;
}

export default InspectionTypeForm;
